import type { Track } from '../../types';

export const MUSIC_PLAYLIST_ORDER_KEY = 'soundpill_playlist_order';

/**
 * 화면·재생 큐 순서: 위에 보이는 곡 = 1번(인덱스 0).
 * 서버는 multiple-tracks 요청 시 각 곡을 큐 앞에 넣으므로,
 * 화면 순서대로 재생하려면 id 배열을 역순으로 보낸다.
 */
export function tracksToQueueTrackIds(tracks: Track[]): number[] {
  return tracks.map((track) => track.id).reverse();
}

/**
 * GET /playlist 응답 → 플레이어·목록 UI 순서(위=1번).
 * API는 최근 추가 곡이 앞에 오는 배열로 내려주는 경우가 많아 한 번 뒤집는다.
 */
export function playlistFromApiResponse(list: Track[] | undefined | null): Track[] {
  const items = list ?? [];
  if (items.length <= 1) return items;
  return [...items].reverse();
}

export function readSavedPlaylistOrder(): Array<number | string> {
  try {
    const raw = window.sessionStorage.getItem(MUSIC_PLAYLIST_ORDER_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (id): id is number | string => typeof id === 'number' || typeof id === 'string',
    );
  } catch {
    return [];
  }
}

export function persistPlaylistOrder(tracks: Track[]): void {
  if (tracks.length === 0) return;
  window.sessionStorage.setItem(MUSIC_PLAYLIST_ORDER_KEY, JSON.stringify(tracks.map((track) => track.id)));
}

export function clearPlaylistOrder(): void {
  window.sessionStorage.removeItem(MUSIC_PLAYLIST_ORDER_KEY);
}

/** 저장된 id 순서대로 API 트랙 배열 재정렬 (새로고침 복구용) */
export function orderTracksByIds(tracks: Track[], orderedIds: Array<number | string>): Track[] {
  if (tracks.length === 0 || orderedIds.length === 0) return tracks;
  const byId = new Map(tracks.map((track) => [String(track.id), track]));
  const ordered = orderedIds
    .map((id) => byId.get(String(id)))
    .filter((track): track is Track => !!track);
  if (ordered.length === 0) return tracks;
  const known = new Set(orderedIds.map(String));
  const appended = tracks.filter((track) => !known.has(String(track.id)));
  return [...ordered, ...appended];
}

/** API 응답 순서가 어긋나도, 사용자가 보던 순서(source)를 유지 */
export function mergeTracksPreservingOrder(source: Track[], apiTracks: Track[]): Track[] {
  if (source.length === 0) return apiTracks;
  return orderTracksByIds(apiTracks, source.map((track) => track.id));
}
